import axios, { AxiosInstance } from 'axios';
import {
  RunStockScanResult,
  GetLatestScanResultsResponse,
  ScanResultDto,
  ErrorResponse,
} from '../types/api';

/**
 * API Client for Trading Scanner Backend
 * Handles all HTTP communication with the .NET backend
 */
class TradingScannerApi {
  private client: AxiosInstance;
  private baseUrl: string;

  constructor(baseUrl: string = 'http://localhost:5000') {
    this.baseUrl = baseUrl;
    this.client = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
      // A full scan makes ~500 sequential Upstox candle requests and can take a few minutes.
      timeout: 300000,
      // Required: sends the HttpOnly session cookie set by the backend after OAuth
      withCredentials: true,
    });

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.data) {
          console.error('API Error:', error.response.data);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Trigger a stock scan
   * POST /api/scans/run
   */
  async runScan(): Promise<RunStockScanResult> {
    try {
      const response = await this.client.post<RunStockScanResult>('/api/scans/run');
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to run scan');
      throw error;
    }
  }

  /**
   * Get latest scan results
   * GET /api/scans/latest
   *
   * @param classification - Filter by 'BUY', 'HOLD', 'SELL', or 'NONE' (optional)
   * @param limit - Maximum results to return (default 1000)
   */
  async getLatestResults(
    classification?: string,
    limit: number = 1000
  ): Promise<GetLatestScanResultsResponse> {
    try {
      const params: Record<string, any> = { limit };
      if (classification) {
        params.classification = classification;
      }

      const response = await this.client.get<GetLatestScanResultsResponse>(
        '/api/scans/latest',
        { params }
      );
      return response.data;
    } catch (error) {
      this.handleError(error, 'Failed to fetch latest results');
      throw error;
    }
  }

  /**
   * Fetch Upstox portfolio holdings symbols via backend proxy.
   * If token is provided it is sent as Authorization header (overrides server config).
   * GET /api/symbols/holdings
   */
  async getHoldings(token?: string): Promise<string[]> {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await this.client.get<{ symbols: string[]; count: number }>(
        '/api/symbols/holdings',
        { headers }
      );
      return response.data.symbols;
    } catch (error) {
      this.handleError(error, 'Failed to fetch holdings');
      throw error;
    }
  }

  /**
   * Health check - verify API is running
   */
  async healthCheck(): Promise<boolean> {
    try {
      await this.client.get('/health');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get the current signed-in user, or null if not authenticated.
   * GET /api/auth/me
   */
  async getMe(): Promise<{ email: string; name: string } | null> {
    try {
      const response = await this.client.get<{ email: string; name: string }>('/api/auth/me');
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Redirect the browser to start Google sign-in. Must be a full navigation
   * (not an axios call) so the OAuth redirect chain and Set-Cookie work.
   */
  login(): void {
    window.location.href = `${this.baseUrl}/api/auth/login?returnUrl=${encodeURIComponent(window.location.origin)}`;
  }

  /**
   * Redirect the browser to sign out and clear the auth cookie.
   */
  logout(): void {
    window.location.href = `${this.baseUrl}/api/auth/logout`;
  }

  /**
   * Get the base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Update the base URL (useful for env changes)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
    this.client.defaults.baseURL = url;
  }

  /**
   * Handle and log API errors
   */
  private handleError(error: any, message: string): void {
    if (error.response?.status === 400) {
      const data: ErrorResponse = error.response.data;
      console.error(`${message} (400):`, data.message);
      if (data.errors) {
        console.error('Validation Errors:', data.errors);
      }
    } else if (error.response?.status === 404) {
      console.error(`${message} (404): Resource not found`);
    } else if (error.response?.status === 500) {
      console.error(`${message} (500): Server error`);
    } else if (error.message === 'timeout of 30000ms exceeded') {
      console.error(`${message}: Request timeout`);
    } else {
      console.error(`${message}:`, error.message);
    }
  }
}

// Export singleton instance
export const api = new TradingScannerApi(
  process.env.REACT_APP_API_URL || 'http://localhost:5000'
);

export default api;
